import type { UiTimesheetEntry, WeeklyTaskRow } from '../models/pm-timesheet.models';

export function sanitizePmHierarchyLabel(value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  if (trimmed === '[object Object]') return '';
  if (trimmed === '—') return '';
  return trimmed;
}

export function pmEntryComment(e: Pick<UiTimesheetEntry, 'comment'>): string {
  return (e.comment ?? '').trim();
}

export function pmEntryPhaseName(e: Pick<UiTimesheetEntry, 'stage'>): string {
  return sanitizePmHierarchyLabel(e.stage);
}

export function pmEntryPhaseDescription(e: Pick<UiTimesheetEntry, 'stageDescription'>): string {
  return sanitizePmHierarchyLabel(e.stageDescription);
}

export function pmEntryMilestoneName(e: Pick<UiTimesheetEntry, 'milestone'>): string {
  return sanitizePmHierarchyLabel(e.milestone);
}

export function pmEntryMilestoneDescription(e: Pick<UiTimesheetEntry, 'milestoneDescription'>): string {
  return sanitizePmHierarchyLabel(e.milestoneDescription);
}

export function formatPmNameWithDescription(name: string, description: string): string {
  const label = (name ?? '').trim();
  const desc = (description ?? '').trim();
  if (!label) return '';
  if (!desc) return label;
  return `${label} (${desc})`;
}

export function pmEntryHasTaskMeta(
  e: Pick<UiTimesheetEntry, 'comment' | 'stage' | 'milestone'>,
): boolean {
  return !!(pmEntryComment(e) || pmEntryPhaseName(e) || pmEntryMilestoneName(e));
}

export function firstEntryInWeeklyRow(row: WeeklyTaskRow): UiTimesheetEntry | null {
  for (const es of Object.values(row.entriesByDate)) {
    if (es?.length) return es[0] ?? null;
  }
  return null;
}

export function weeklyRowHasTaskMeta(row: WeeklyTaskRow): boolean {
  const e = firstEntryInWeeklyRow(row);
  return e ? pmEntryHasTaskMeta(e) : false;
}

/**
 * Backend may send phase/milestone as a string or nested object ({ phaseName, description, … }).
 */
export function coercePmHierarchyLabel(value: unknown, kind: 'phase' | 'milestone'): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '[object Object]' ? '' : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'object') return '';

  const rec = value as Record<string, unknown>;
  const keys =
    kind === 'phase'
      ? [
          'phaseName',
          'PhaseName',
          'phase_name',
          'stageName',
          'StageName',
          'stage_name',
          'name',
          'Name',
          'label',
          'Label',
          'title',
          'Title',
        ]
      : [
          'milestoneName',
          'MilestoneName',
          'milestone_name',
          'name',
          'Name',
          'label',
          'Label',
          'title',
          'Title',
        ];

  for (const key of keys) {
    const nested = rec[key];
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return '';
}

export function coercePmHierarchyDescription(value: unknown, kind: 'phase' | 'milestone'): string {
  if (value == null || typeof value !== 'object') return '';

  const rec = value as Record<string, unknown>;
  const keys =
    kind === 'phase'
      ? [
          'phaseDescription',
          'PhaseDescription',
          'phase_description',
          'stageDescription',
          'StageDescription',
          'stage_description',
          'description',
          'Description',
        ]
      : [
          'milestoneDescription',
          'MilestoneDescription',
          'milestone_description',
          'description',
          'Description',
        ];

  for (const key of keys) {
    const nested = rec[key];
    if (typeof nested === 'string') {
      const trimmed = nested.trim();
      if (trimmed && trimmed !== '[object Object]') return trimmed;
    }
  }
  return '';
}

export function resolvePmTaskPhaseName(rec: Record<string, unknown>): string {
  const candidates = [
    rec['phase'],
    rec['Phase'],
    rec['phase_name'],
    rec['phaseName'],
    rec['stage'],
    rec['Stage'],
    rec['stage_name'],
    rec['stageName'],
  ];
  for (const candidate of candidates) {
    const label = coercePmHierarchyLabel(candidate, 'phase');
    if (label) return label;
  }
  return '';
}

export function resolvePmTaskPhaseDescription(rec: Record<string, unknown>): string {
  const topLevel = [
    rec['phase_description'],
    rec['phaseDescription'],
    rec['PhaseDescription'],
    rec['stage_description'],
    rec['stageDescription'],
    rec['StageDescription'],
  ];
  for (const candidate of topLevel) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  const objectCandidates = [rec['phase'], rec['Phase'], rec['stage'], rec['Stage']];
  for (const candidate of objectCandidates) {
    const desc = coercePmHierarchyDescription(candidate, 'phase');
    if (desc) return desc;
  }
  return '';
}

export function resolvePmTaskMilestoneName(rec: Record<string, unknown>): string {
  const candidates = [
    rec['milestone'],
    rec['Milestone'],
    rec['milestone_name'],
    rec['milestoneName'],
    rec['ms'],
  ];
  for (const candidate of candidates) {
    const label = coercePmHierarchyLabel(candidate, 'milestone');
    if (label) return label;
  }
  return '';
}

export function resolvePmTaskMilestoneDescription(rec: Record<string, unknown>): string {
  const topLevel = [
    rec['milestone_description'],
    rec['milestoneDescription'],
    rec['MilestoneDescription'],
  ];
  for (const candidate of topLevel) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  const objectCandidates = [rec['milestone'], rec['Milestone']];
  for (const candidate of objectCandidates) {
    const desc = coercePmHierarchyDescription(candidate, 'milestone');
    if (desc) return desc;
  }
  return '';
}
